import { memo } from "react";
import { useDispatch } from "react-redux";

import { EvaSource } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { updateChartByPath } from "store/features";
import { capitalizeFirstLetter } from "utils";

import { useUserEvents } from "hooks";
import { useChartDependencies, useChartEntities } from "hooks/charts";
import useFeatureFlags from "hooks/useFeatureFlags";

import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { DataSourceIcon } from "components/shared";

import ToolbarButton from "../../base/ToolbarButton";
import ChartDataSourceSelect from "./ChartDataSourceSelect";

const DataSourceToggle = () => {
  const dispatch = useDispatch();
  const { id, entityKind } = useChartsContext();
  const { source: sourceState, chartTypeParam, chartType } = useChartEntities(id);
  const { pdenDataSourceSetting } = useChartDependencies(entityKind);
  const { sendChartDataSourceEvent } = useUserEvents();
  const { hasFeature } = useFeatureFlags();

  // EVA-334: hide only — the chart's persisted source stays in effect.
  const showButton = sourceState?.show && hasFeature("Data Source Toggles");
  const isEnabled = sourceState?.enabled ?? true;
  const source = (sourceState?.properties?.value ?? EvaSource.Public) as EvaSource;
  // gDC == 0 — guard with != null; never use truthiness check
  const specificPublicSource = sourceState?.properties?.specificPublicSource;
  const icon = DataSourceIcon[source];
  const label = capitalizeFirstLetter(source);

  function handleSourceChange(value: EvaSource) {
    dispatch(
      updateChartByPath({
        id,
        path: `features.source.properties.value`,
        value: value
      })
    );

    // Clear the per-chart specific-source override whenever the high-level source mode changes.
    dispatch(
      updateChartByPath({
        id,
        path: `features.source.properties.specificPublicSource`,
        value: undefined
      })
    );

    sendChartDataSourceEvent({
      chartType: chartType,
      entityKind: entityKind,
      dataSource: value,
      pdenSource: pdenDataSourceSetting?.source
    });

    // if sameSource, set pdenSource to undefined so chart will follow global source again
    const sameSource = value === pdenDataSourceSetting?.source;

    if (chartTypeParam.properties.x.pdenSource || sameSource) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.x.pdenSource`,
          value: sameSource ? undefined : PdenSourceEnum[value] ?? undefined
        })
      );
    } else if (!chartTypeParam.properties.x.pdenSource || sameSource) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.x`,
          value: {
            ...chartTypeParam.properties.x,
            pdenSource: sameSource ? undefined : PdenSourceEnum[value] ?? undefined
          }
        })
      );
    }
    if (chartTypeParam.properties.y.pdenSource || sameSource) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.y.pdenSource`,
          value: sameSource ? undefined : PdenSourceEnum[value] ?? undefined
        })
      );
    } else if (!chartTypeParam.properties.y.pdenSource || sameSource) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.chartTypeParam.properties.y`,
          value: {
            ...chartTypeParam.properties.y,
            pdenSource: sameSource ? undefined : PdenSourceEnum[value] ?? undefined
          }
        })
      );
    }
  }

  function handleSpecificSourceChange(value: number | undefined) {
    dispatch(
      updateChartByPath({
        id,
        path: `features.source.properties.specificPublicSource`,
        value: value
      })
    );
  }

  if (!showButton) {
    return null;
  }

  return (
    <ChartDataSourceSelect
      source={source}
      specificPublicSource={specificPublicSource}
      onSourceChange={handleSourceChange}
      onSpecificSourceChange={handleSpecificSourceChange}
      trigger={["hover"]}
      disabled={!isEnabled}>
      <ToolbarButton
        data-testid="datasource-trigger"
        aria-label={source}
        active={false}
        icon={icon}
        overflowLabel={label}
        disabled={!isEnabled}
      />
    </ChartDataSourceSelect>
  );
};

export default memo(DataSourceToggle);
